import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type ComplianceCalendarResponse as CalendarResponseType, TimelineEvent } from "@/types/compliance";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar, 
  AlertTriangle, 
  FileText, 
  Clock, 
  Info, 
  Check, 
  X, 
  ListFilter, 
  BarChart3, 
  LucideIcon,
  CalendarRange, 
  FilePlus,
  BarChart,
  Filter,
  Download
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { API_CONFIG } from "@/constants/env";

interface ComplianceCalendarResponseProps {
  response: CalendarResponseType;
  onReset: () => void;
}

// Tab interface
interface TabDefinition {
  id: string;
  label: string;
  icon: LucideIcon;
  count?: number;
}

const ComplianceCalendarResponse = ({ response, onReset }: ComplianceCalendarResponseProps) => {
  // Main section tabs
  const mainTabs: TabDefinition[] = [
    { id: "overview", label: "Summary", icon: BarChart },
    { id: "timeline", label: "Timeline", icon: Calendar, count: response.timeline.length },
    { id: "regulations", label: "Current Regulations", icon: FileText, count: response.current_regulations.length },
    { id: "upcoming", label: "Upcoming Changes", icon: AlertTriangle, count: response.upcoming_changes.length },
  ];

  const [activeMainTab, setActiveMainTab] = useState<string>("overview");
  const [activeYear, setActiveYear] = useState<string>("");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activePriority, setActivePriority] = useState<string>("all");
  
  // PDF download states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  
  // Group timeline events by year
  const timelineByYear = useMemo(() => {
    const years: Record<string, TimelineEvent[]> = {};
    
    // Sort timeline by date
    const filteredTimeline = response.timeline.filter(event => event.date && typeof event.date === 'string');
    const sortedTimeline = [...filteredTimeline].sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
    
    // Group by year
    sortedTimeline.forEach(event => {
      if (!event.date) return; // Skip if date is undefined
      
      const year = event.date.split('-')[0];
      if (!years[year]) {
        years[year] = [];
      }
      years[year].push(event);
    });
    
    return years;
  }, [response.timeline]);
  
  // Set first year as active by default
  React.useEffect(() => {
    if (Object.keys(timelineByYear).length > 0 && !activeYear) {
      setActiveYear(Object.keys(timelineByYear)[0]);
    }
  }, [timelineByYear, activeYear]);

  // Process timeline events with filter logic
  const filteredTimelineEvents = useMemo(() => {
    if (!activeYear || !timelineByYear[activeYear]) return [];
    
    return timelineByYear[activeYear].filter(event => {
      if (activeCategory !== 'all' && event.category !== activeCategory) return false;
      if (activePriority !== 'all' && event.priority !== activePriority) return false;
      return true;
    });
  }, [timelineByYear, activeYear, activeCategory, activePriority]);

  // Get unique categories for the timeline filter
  const timelineCategories = useMemo(() => {
    const categories = new Set<string>();
    response.timeline.forEach(event => {
      if (event && event.category) {
        categories.add(event.category);
      }
    });
    return Array.from(categories);
  }, [response.timeline]);

  // Overall assessment display function
  const renderOverallAssessment = () => {
    if (typeof response.summary === 'string') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <p className="text-gray-700 dark:text-gray-300">{response.summary}</p>
        </div>
      );
    }
    
    if (!response.summary.overall_assessment) return null;
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <h3 className="text-lg font-semibold mb-3">Overall Assessment</h3>
        <p className="text-gray-700 dark:text-gray-300">{response.summary.overall_assessment}</p>
      </div>
    );
  };
  
  // Priority counters for timeline
  const priorityCounts = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    response.timeline.forEach(event => {
      if (event && event.priority && (counts[event.priority] !== undefined)) {
        counts[event.priority] += 1;
      }
    });
    return counts;
  }, [response.timeline]);

  // Group regulations by category
  const regulationsByCategory = useMemo(() => {
    const categories: Record<string, number> = {};
    response.current_regulations.forEach(reg => {
      if (reg && reg.department && typeof reg.department === 'string') {
        const department = reg.department.split('/')[0];
        categories[department] = (categories[department] || 0) + 1;
      }
    });
    return categories;
  }, [response.current_regulations]);

  // Render Overview Tab Content
  const renderOverviewTab = () => (
    <div className="p-6 space-y-6">
      {/* Summary Card */}
      <div>
        <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          Compliance Summary Dashboard
        </h2>
        
        <div className="space-y-6">
          {/* Overall Assessment */}
          {renderOverallAssessment()}
          
          {/* Key Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Requirements</p>
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{response.current_regulations.length}</h4>
                </div>
                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Current applicable regulations</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Upcoming Deadlines</p>
                  <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{response.timeline.length}</h4>
                </div>
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-full">
                  <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Scheduled compliance tasks</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">High Priority Tasks</p>
                  <h4 className="text-2xl font-bold text-red-600 dark:text-red-400">{priorityCounts.high}</h4>
                </div>
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Require immediate attention</p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Upcoming Changes</p>
                  <h4 className="text-2xl font-bold text-amber-600 dark:text-amber-400">{response.upcoming_changes.length}</h4>
                </div>
                <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-full">
                  <FilePlus className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">New regulatory frameworks</p>
            </div>
          </div>

          {/* Summary Details */}
          {typeof response.summary !== 'string' && (
            <>
              {/* Critical Compliance Areas */}
              {response.summary.critical_compliance_areas && response.summary.critical_compliance_areas.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                  <h3 className="text-lg font-semibold mb-3">Critical Compliance Areas</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {response.summary.critical_compliance_areas.map((area, idx) => (
                      <li key={idx} className="text-gray-700 dark:text-gray-300">{area}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Strategy Recommendations */}
              {response.summary.recommended_strategy && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                  <h3 className="text-lg font-semibold mb-3">Recommended Strategy</h3>
                  <p className="text-gray-700 dark:text-gray-300">{response.summary.recommended_strategy}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Render Timeline Tab Content
  const renderTimelineTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <Calendar className="h-5 w-5 text-blue-500" />
        Compliance Timeline
      </h2>
      
      {/* Filter Controls */}
      <div className="flex flex-col md:flex-row gap-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="space-y-2 flex-1">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <CalendarRange className="h-4 w-4 text-gray-500" />
            Year
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.keys(timelineByYear).map(year => (
              <Button 
                key={year}
                onClick={() => setActiveYear(year)}
                variant={activeYear === year ? "default" : "outline"}
                size="sm"
                className={activeYear === year ? "bg-blue-500 hover:bg-blue-600" : ""}
              >
                {year}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="space-y-2 flex-1">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <ListFilter className="h-4 w-4 text-gray-500" />
            Category
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={() => setActiveCategory('all')}
              variant={activeCategory === 'all' ? "default" : "outline"}
              size="sm"
              className={activeCategory === 'all' ? "bg-blue-500 hover:bg-blue-600" : ""}
            >
              All
            </Button>
            {timelineCategories.map(category => (
              <Button 
                key={category}
                onClick={() => setActiveCategory(category)}
                variant={activeCategory === category ? "default" : "outline"}
                size="sm"
                className={activeCategory === category ? "bg-blue-500 hover:bg-blue-600" : ""}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="space-y-2 flex-1">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <Filter className="h-4 w-4 text-gray-500" />
            Priority
          </h3>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={() => setActivePriority('all')}
              variant={activePriority === 'all' ? "default" : "outline"}
              size="sm"
              className={activePriority === 'all' ? "bg-blue-500 hover:bg-blue-600" : ""}
            >
              All
            </Button>
            <Button 
              onClick={() => setActivePriority('high')}
              variant={activePriority === 'high' ? "default" : "outline"}
              size="sm"
              className={activePriority === 'high' ? "bg-red-500 hover:bg-red-600" : "text-red-600 border-red-200"}
            >
              High ({priorityCounts.high})
            </Button>
            <Button 
              onClick={() => setActivePriority('medium')}
              variant={activePriority === 'medium' ? "default" : "outline"}
              size="sm"
              className={activePriority === 'medium' ? "bg-amber-500 hover:bg-amber-600" : "text-amber-600 border-amber-200"}
            >
              Medium ({priorityCounts.medium})
            </Button>
            <Button 
              onClick={() => setActivePriority('low')}
              variant={activePriority === 'low' ? "default" : "outline"}
              size="sm"
              className={activePriority === 'low' ? "bg-blue-500 hover:bg-blue-600" : "text-blue-600 border-blue-200"}
            >
              Low ({priorityCounts.low})
            </Button>
          </div>
        </div>
      </div>
      
      {/* Timeline Events */}
      <div className="space-y-6">
        {filteredTimelineEvents.length > 0 ? (
          filteredTimelineEvents.map((event, index) => (
            <div 
              key={index} 
              className={`relative pl-8 pb-6 border-l-2 ${
                event.priority === 'high' 
                  ? 'border-red-400 dark:border-red-600' 
                  : event.priority === 'medium'
                    ? 'border-amber-400 dark:border-amber-600'
                    : 'border-blue-400 dark:border-blue-600'
              }`}
            >
              {/* Event dot */}
              <div 
                className={`absolute left-[-10px] top-0 h-5 w-5 rounded-full ${
                  event.priority === 'high' 
                    ? 'bg-red-400 dark:bg-red-600' 
                    : event.priority === 'medium'
                      ? 'bg-amber-400 dark:bg-amber-600'
                      : 'bg-blue-400 dark:bg-blue-600'
                }`}
              />
              
              {/* Event content */}
              <div className="bg-white dark:bg-gray-800 rounded-md p-4 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex flex-wrap justify-between mb-2">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {event.formatted_date || event.date}
                  </div>
                  <div className={`text-sm font-medium px-2 py-0.5 rounded-full ${
                    event.priority === 'high' 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' 
                      : event.priority === 'medium'
                        ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  }`}>
                    {event.priority.charAt(0).toUpperCase() + event.priority.slice(1)} Priority
                  </div>
                </div>
                
                <h3 className="text-lg font-medium mb-2">{event.regulation}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-3">{event.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-300">Required Action:</div>
                    <div className="mt-1 text-gray-600 dark:text-gray-400">{event.action_required}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700 dark:text-gray-300">Responsible:</div>
                    <div className="mt-1 text-gray-600 dark:text-gray-400">{event.department}</div>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center">
                  <div 
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      event.category === 'reporting' 
                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' 
                        : event.category === 'implementation'
                          ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                          : event.category === 'audit'
                            ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}
                  >
                    {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No Events Found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {activeYear && activeCategory !== 'all' && activePriority !== 'all' 
                ? `No ${activePriority} priority ${activeCategory} events found for ${activeYear}.` 
                : `No events match your current filters.`}
            </p>
            <Button 
              onClick={() => {
                setActiveCategory('all');
                setActivePriority('all');
              }} 
              variant="outline" 
              size="sm" 
              className="mt-4"
            >
              Reset Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  // Render Regulations Tab Content
  const renderRegulationsTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <FileText className="h-5 w-5 text-blue-500" />
        Current Applicable Regulations
      </h2>

      {/* Category breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Regulations by Department</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(regulationsByCategory).map(([category, count]) => (
            <Badge key={category} variant="outline" className="px-3 py-1">
              {category}: <span className="font-semibold ml-1">{count}</span>
            </Badge>
          ))}
        </div>
      </div>
      
      {/* Regulations accordion */}
      <Accordion type="multiple" className="space-y-4">
        {response.current_regulations.map((regulation, index) => (
          <AccordionItem 
            key={index} 
            value={`regulation-${index}`}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="px-5 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/80">
              <div className="flex flex-col items-start text-left">
                <h3 className="text-lg font-semibold">{regulation.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{regulation.department}</p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 py-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <p className="text-gray-600 dark:text-gray-300 mb-4">{regulation.description}</p>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Key Requirements:</h4>
                <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  {regulation.key_requirements.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-md">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Deadlines: </span>
                  <span className="text-gray-600 dark:text-gray-400">{regulation.deadlines}</span>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-md">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Department: </span>
                  <span className="text-gray-600 dark:text-gray-400">{regulation.department}</span>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {response.current_regulations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No Current Regulations</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">No applicable regulations were identified for your organization.</p>
        </div>
      )}
    </div>
  );

  // Render Upcoming Changes Tab Content
  const renderUpcomingChangesTab = () => (
    <div className="p-6 space-y-6">
      <h2 className="text-xl font-bold pb-2 border-b flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-blue-500" />
        Upcoming Regulatory Changes
      </h2>

      {/* Impact filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant="outline" size="sm" className="bg-white dark:bg-gray-800">
          All Changes
        </Button>
        <Button variant="outline" size="sm" className="border-red-200 text-red-600">
          High Impact
        </Button>
        <Button variant="outline" size="sm" className="border-amber-200 text-amber-600">
          Medium Impact
        </Button>
        <Button variant="outline" size="sm" className="border-blue-200 text-blue-600">
          Low Impact
        </Button>
      </div>
      
      {/* Changes cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {response.upcoming_changes.map((change, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="p-5">
              <div className="flex flex-wrap justify-between items-start mb-3">
                <h3 className="text-lg font-semibold">{change.name}</h3>
                <div className="text-sm bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-md flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {change.implementation_date}
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{change.description}</p>
              
              {/* Impact badge */}
              {change.impact && (
                <div className="mb-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                    change.impact.toLowerCase().includes('high') 
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' 
                      : change.impact.toLowerCase().includes('medium') 
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  }`}>
                    <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                    {change.impact}
                  </span>
                </div>
              )}
              
              <Accordion type="single" collapsible className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                <AccordionItem value="details">
                  <AccordionTrigger className="px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/50 text-sm font-medium">
                    View Details
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3 bg-white dark:bg-gray-800/50 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      {change.transition_period && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">Transition Period: </span>
                          <span className="text-gray-600 dark:text-gray-400">{change.transition_period}</span>
                        </div>
                      )}
                      
                      {change.first_reporting_deadline && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">First Reporting Deadline: </span>
                          <span className="text-gray-600 dark:text-gray-400">{change.first_reporting_deadline}</span>
                        </div>
                      )}
                      
                      {change.key_dates && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">Key Dates: </span>
                          <span className="text-gray-600 dark:text-gray-400">{change.key_dates}</span>
                        </div>
                      )}
                    </div>
                    
                    {change.changes && change.changes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Key Changes:</h4>
                        <ul className="list-disc pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          {change.changes.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        ))}
      </div>

      {response.upcoming_changes.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <AlertTriangle className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No Upcoming Changes</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">No upcoming regulatory changes were identified for your organization.</p>
        </div>
      )}
    </div>
  );

  // Handle PDF download request
  const handlePdfRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setSubmitError("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      const result = await fetch(`${API_CONFIG.API_BASE_URL}/compliance-calendar/pdf/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          ai_response: response
        }),
      });
      
      if (!result.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      setShowEmailDialog(false);
      setEmail("");
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error requesting PDF:', error);
      setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render Content Based on Active Tab
  const renderActiveTabContent = () => {
    switch (activeMainTab) {
      case "overview":
        return renderOverviewTab();
      case "timeline":
        return renderTimelineTab();
      case "regulations":
        return renderRegulationsTab();
      case "upcoming":
        return renderUpcomingChangesTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons - Moved to the top */}
      <div className="flex justify-center gap-4 mb-6">
        <Button onClick={onReset} variant="outline" size="lg">
          Generate New Calendar
        </Button>
        <Button 
          onClick={() => setShowEmailDialog(true)} 
          variant="default" 
          size="lg" 
          className="bg-purple-600 hover:bg-purple-500"
        >
          <Download className="mr-2 h-4 w-4" /> Download as PDF
        </Button>
      </div>
      
      <Card className="border-border/40 bg-background/60  overflow-hidden border-t-4 border-b-4 border-t-blue-500 border-b-blue-500">
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
          <div className="border-b border-border/50">
            <TabsList className="w-full flex justify-between rounded-none bg-transparent border-b p-0">
              {mainTabs.map((tab) => (
                <TabsTrigger 
                  key={tab.id} 
                  value={tab.id}
                  className={cn(
                    "flex-1 py-4 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:shadow-none data-[state=active]:text-blue-700 dark:data-[state=active]:text-blue-400",
                    "bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50 gap-2 font-medium",
                    "border border-gray-200 dark:border-gray-700 border-b-0 mx-0.5 first:ml-0 last:mr-0",
                    "shadow-sm"
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <Badge variant="outline" className="ml-1 text-xs font-normal">
                      {tab.count}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value={activeMainTab} className="mt-0">
            {renderActiveTabContent()}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Download PDF Report</DialogTitle>
            <DialogDescription>
              Enter your email address where we should send the PDF version of this compliance calendar.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePdfRequest}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Input
                  id="email" 
                  className="col-span-4"
                  placeholder="your.email@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              {submitError && (
                <p className="text-red-500 text-sm">{submitError}</p>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-purple-600 hover:bg-purple-500"
              >
                {isSubmitting ? 'Sending...' : 'Send PDF'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Success!</DialogTitle>
            <DialogDescription>
              Your compliance calendar PDF has been successfully requested.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center justify-center py-6">
            <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
              <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          
          <p className="text-center text-gray-600 dark:text-gray-300">
            The PDF will be sent to your email shortly. Please check your inbox.
          </p>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowSuccessDialog(false)}
              className="bg-green-600 hover:bg-green-500 mt-2"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceCalendarResponse;